import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { userPool as userpool, PRIVACY_POLICY_URL } from '../constants/constants';
import { CognitoUserAttribute, CognitoUser } from 'amazon-cognito-identity-js';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { getGoogleOAuthUrl } from '../authConfig';
import { talktreesSignupIntentUrl } from '../apiConfig';
import { COLORS, FONT_FAMILY } from '../talkTreeShared/Tree/constants/theme';

const cognitoAppClientId = userpool.clientId;

const defaultTheme = createTheme({
  typography: {
    fontFamily: FONT_FAMILY,
  },
});

const STEPS = ['Your details', 'Choose sign-up method'];

/** Official multi-color Google "G" mark for the sign-up button. */
function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const GreenTick = () => (
  <InputAdornment position="end" aria-label="Valid">
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: COLORS.success,
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      role="img"
      aria-label="Valid"
    >
      ✓
    </Box>
  </InputAdornment>
);

const RedError = () => (
  <InputAdornment position="end" aria-label="Invalid">
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: COLORS.error,
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      role="img"
      aria-label="Invalid"
    >
      !
    </Box>
  </InputAdornment>
);

export default function SignUp() {
  const navigate = useNavigate();

  /* global mixpanel */
  useEffect(() => { mixpanel.track('signupPageViewed'); }, []);

  // Load Mulish once (safe no-op if already linked in index.html).
  useEffect(() => {
    const id = 'mulish-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  const [wizardStep, setWizardStep] = useState(0);

  const [noSpaces, setNoSpaces] = useState(false);
  const [password, setPassword] = useState('');
  const [pwMin8, setPwMin8] = useState(false);
  const [pwLC1, setPwLC1] = useState(false);
  const [pwUC1, setPwUC1] = useState(false);
  const [pwNum1, setPwNum1] = useState(false);
  const [pwSC1, setPwSC1] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailAgain, setEmailAgain] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCodeHelp, setShowCodeHelp] = useState(false);

  const validatePassword = () => !noSpaces && pwMin8 && pwLC1 && pwUC1 && pwNum1 && pwSC1;

  const emailMatch = email.length > 0 && emailAgain.length > 0 && email === emailAgain;
  const emailMismatch = emailAgain.length > 0 && email !== emailAgain;
  const confirmMatch = confirmPassword.length > 0 && password === confirmPassword;
  const confirmMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#ffffff',
    },
  };

  const pwChecks = useMemo(
    () => [
      { label: '8 Characters Minimum', ok: pwMin8 },
      { label: '1 lower case letter', ok: pwLC1 },
      { label: '1 upper case letter', ok: pwUC1 },
      { label: '1 number', ok: pwNum1 },
      { label: '1 special character', ok: pwSC1 },
    ],
    [pwMin8, pwLC1, pwUC1, pwNum1, pwSC1]
  );

  const handleStep1Continue = async (e) => {
    e.preventDefault();
    setError('');
    if (firstName.trim().length < 1 || lastName.trim().length < 1) {
      setError('Please enter your first and last name.');
      return;
    }
    if (registrationCode.trim().length < 6) {
      setError('Please enter a valid registration code.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(talktreesSignupIntentUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          registrationCode: registrationCode.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        mixpanel.track('signupFailed', { step: 1, method: null, error: data.error || 'Invalid registration code', code: registrationCode.trim(), givenName: firstName.trim(), familyName: lastName.trim() });
        setError(data.error || 'Invalid registration code. Please try again.');
        return;
      }
    } catch {
      mixpanel.track('signupFailed', { step: 1, method: null, error: 'Network error validating registration code' });
      setError('Could not validate registration code. Please try again.');
      return;
    } finally {
      setLoading(false);
    }

    mixpanel.track('signupStep1Completed', { givenName: firstName.trim(), familyName: lastName.trim() });
    setWizardStep(1);
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(false);
    mixpanel.track('signupMethodSelected', { method: 'Google' });

    sessionStorage.setItem('signup_given_name', firstName.trim());
    sessionStorage.setItem('signup_family_name', lastName.trim());

    const redirect = `${window.location.origin}/signup/callback`;
    window.location.href = getGoogleOAuthUrl(
      cognitoAppClientId,
      redirect,
      registrationCode.trim()
    );
  };

  const validateEmailSignUpFields = () =>
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    email.trim().length > 0 &&
    email === emailAgain &&
    registrationCode.trim().length > 5 &&
    validatePassword() &&
    password.length > 7 &&
    password === confirmPassword;

  const handleSignUp = (event) => {
    event.preventDefault();
    setError('');

    if (!validateEmailSignUpFields()) {
      setError('Please fill in all fields correctly.');
      return;
    }

    setLoading(true);
    const normalizedEmail = email.toLowerCase().trim();
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: normalizedEmail }),
      new CognitoUserAttribute({ Name: 'given_name', Value: firstName.trim() }),
      new CognitoUserAttribute({ Name: 'family_name', Value: lastName.trim() }),
      new CognitoUserAttribute({
        Name: 'custom:registrationCode',
        Value: registrationCode.trim(),
      }),
    ];

    const validationData = [{ Name: 'registrationCode', Value: registrationCode.trim() }];
    userpool.signUp(email.trim(), password, attributeList, validationData, (err, result) => {
      setLoading(false);
      if (err) {
        if (err.code === 'UsernameExistsException') {
          mixpanel.track('signupFailed', { step: 2, method: 'Email', error: 'Username already exists' });
          setError('An account with this email already exists.');
        } else {
          mixpanel.track('signupFailed', { step: 2, method: 'Email', error: err.message || 'An error occurred during sign-up.' });
          setError(err.message || 'An error occurred during sign-up.');
        }
        return;
      }
      if (result && result.userConfirmed) {
        try {
          sessionStorage.setItem('signupSuccess', '1');
        } catch {
          // no-op
        }
        // case: User email is automatically confirmed; they can log in immediately.
        mixpanel.identify(result.user.getUsername());
        mixpanel.people.set_once({ signupMethod: 'Email', signedUpAt: new Date().toISOString() });
        mixpanel.track('userSignedUp', { method: 'Email', username: result.user.getUsername() });
        navigate('/login');
        return;
      }
      try {
        sessionStorage.setItem('signupSuccess', '1');
      } catch {
        // no-op
      }
      // case: User is not confirmed yet; they need to enter the verification code sent to their email.
      mixpanel.track('userSignedUpNeedsVerification', { method: 'Email', username: result.user.getUsername() });
      setSignUpSuccess(true);
      setError('');
    });
  };

  const handleVerificationCodeSubmit = (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const cognitoUser = new CognitoUser({ Username: email.trim(), Pool: userpool });
    cognitoUser.confirmRegistration(verificationCode, true, (err) => {
      setLoading(false);
      if (err) {
        setError(err.message || 'Verification failed.');
      } else {
        mixpanel.track('signupEmailVerified');
        navigate('/login');
      }
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setNoSpaces(value.includes(' '));
    setPwMin8(value.length >= 8);
    setPwLC1(/[a-z]/.test(value));
    setPwUC1(/[A-Z]/.test(value));
    setPwNum1(/\d/.test(value));
    setPwSC1(/[!@#$%^&*(),.?":{}|<>]/.test(value));
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', backgroundColor: COLORS.page }}>
        {/* Left illustration panel — lighter, airier background */}
        <Box
          sx={{
            width: { xs: 0, md: '46%' },
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            background: COLORS.panelGradient,
            position: 'sticky',
            top: 0,
            height: '100vh',
          }}
          aria-hidden="true"
        >
          <Box
            component="img"
            src="/SampleTree.png"
            alt=""
            role="presentation"
            sx={{ width: '62%', maxWidth: 420, objectFit: 'contain' }}
          />
        </Box>

        {/* Right form panel — no card, generous whitespace */}
        <Box
          component="main"
          sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}
        >
          <Container maxWidth="sm" disableGutters>
            <Box sx={{ width: '100%' }}>
              {/* Heading with inline TalkTrees mark */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.25,
                  mb: 4,
                }}
              >
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "'Mulish', sans-serif",
                    fontWeight: 500,
                    fontSize: { xs: 26, sm: 32 },
                    letterSpacing: '0px',
                    color: COLORS.primary,
                    lineHeight: 1.1,
                  }}
                >
                  Sign up for
                </Typography>
                <Box
                  component="img"
                  src="/TTLogo.svg"
                  alt="TalkTrees"
                  sx={{ height: { xs: 30, sm: 38 }, width: 'auto', display: 'block', flexShrink: 0 }}
                />
              </Box>

              {!signUpSuccess && (
                <Stepper
                  activeStep={wizardStep}
                  sx={{ width: '100%', mb: 4 }}
                  aria-label={`Step ${wizardStep + 1} of ${STEPS.length}: ${STEPS[wizardStep]}`}
                >
                  {STEPS.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}

              {/* Live region for dynamic status messages */}
              <Box aria-live="polite" aria-atomic="true">
                {error && (
                  <Alert severity="error" sx={{ mb: 2, width: '100%', borderRadius: '10px' }}>
                    {error}
                  </Alert>
                )}
              </Box>

              {!signUpSuccess && wizardStep === 0 && (
                <Box
                  component="form"
                  onSubmit={handleStep1Continue}
                  noValidate
                  sx={{ width: '100%' }}
                  aria-label="Your details"
                  aria-busy={loading}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        id="registration-code"
                        label="Registration Code"
                        value={registrationCode}
                        onChange={(e) => setRegistrationCode(e.target.value)}
                        helperText="(The instructor registration code provided to you.)"
                        inputProps={{ 'aria-required': true, 'aria-describedby': 'registration-code-helper-text' }}
                        sx={fieldSx}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                onClick={() => setShowCodeHelp((prev) => !prev)}
                                aria-label="What is a registration code?"
                                aria-expanded={showCodeHelp}
                              >
                                <HelpOutlineIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {showCodeHelp && (
                        <Alert severity="info" sx={{ mt: 1.5, textAlign: 'left', borderRadius: '10px' }} role="status" >
                          Early adopter access is by invitation only. Enter the registration code that we shared with you or request access at <a href="https://www.talktrees.ai" target="_blank" rel="noopener noreferrer">https://www.talktrees.ai</a>
                        </Alert>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        inputProps={{ 'aria-required': true }}
                        sx={fieldSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        inputProps={{ 'aria-required': true }}
                        sx={fieldSx}
                      />
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                    disabled={loading}
                    sx={{ mt: 3.5, mb: 2, backgroundColor: COLORS.primary, borderRadius: '4px', py: 1.35, fontWeight: 700, fontSize: 15, textTransform: 'none', '&:hover': { backgroundColor: COLORS.primaryHover } }}
                  >
                    {loading ? 'Checking...' : 'Continue'}
                  </Button>
                  <Box sx={{ textAlign: 'center' }}>
                    <span style={{ color: COLORS.textMuted }}>Already have an account?</span>{' '}
                    <Link component={RouterLink} to="/login" variant="body2" style={{ color: COLORS.primary, textDecoration: 'underline', fontWeight: 600 }}>
                      Log in
                    </Link>
                  </Box>
                </Box>
              )}

              {!signUpSuccess && wizardStep === 1 && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ mb: 2.5, color: COLORS.text }}>
                    Your registration is verified. Choose how to create your account.
                  </Typography>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleGoogleSignUp}
                    disabled={loading}
                    startIcon={<GoogleIcon />}
                    sx={{
                      mb: 1.5,
                      borderColor: COLORS.border,
                      color: COLORS.text,
                      borderRadius: '4px',
                      py: 1.2,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { borderColor: COLORS.primary, backgroundColor: COLORS.tintedHover },
                    }}
                  >
                    {loading ? 'Preparing...' : 'Sign up with Google'}
                  </Button>

                  <Divider sx={{ my: 2, color: COLORS.textMuted }} role="separator">or</Divider>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: COLORS.text }}>
                    Sign up using your email address
                  </Typography>

                  <Box
                    component="form"
                    onSubmit={handleSignUp}
                    aria-label="Email sign-up form"
                    aria-busy={loading}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="signup-email"
                          label="Email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="email"
                          inputProps={{ 'aria-required': true }}
                          sx={fieldSx}
                          InputProps={{ endAdornment: emailMatch ? <GreenTick /> : null }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="signup-email-confirm"
                          label="Confirm Email"
                          value={emailAgain}
                          onChange={(e) => setEmailAgain(e.target.value)}
                          error={emailMismatch}
                          helperText={emailMismatch ? "Your email address doesn't match" : ''}
                          inputProps={{
                            'aria-required': true,
                            'aria-invalid': emailMismatch,
                          }}
                          sx={fieldSx}
                          InputProps={{ endAdornment: emailMatch ? <GreenTick /> : emailMismatch ? <RedError /> : null }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          label="Password"
                          value={password}
                          onChange={handlePasswordChange}
                          error={noSpaces}
                          helperText={noSpaces ? 'Password cannot contain spaces' : ''}
                          inputProps={{
                            'aria-required': true,
                            'aria-invalid': noSpaces,
                            'aria-describedby': 'password-requirements',
                          }}
                          sx={fieldSx}
                          InputProps={{
                            endAdornment: (
                              <>
                                {validatePassword() && password.length > 0 && <GreenTick />}
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    edge="end"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              </>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="signup-confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          label="Confirm Password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          error={confirmMismatch}
                          helperText={confirmMismatch ? "Passwords don't match" : ''}
                          inputProps={{
                            'aria-required': true,
                            'aria-invalid': confirmMismatch,
                          }}
                          sx={fieldSx}
                          InputProps={{
                            endAdornment: (
                              <>
                                {confirmMatch && <GreenTick />}
                                {confirmMismatch && <RedError />}
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    edge="end"
                                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                                  >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              </>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Box
                      id="password-requirements"
                      role="group"
                      aria-label="Password requirements"
                      sx={{
                        mt: 2,
                        mb: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px 24px',
                        justifyContent: 'center',
                      }}
                    >
                      {pwChecks.map(({ label, ok }) => (
                        <Box
                          key={label}
                          component="span"
                          role="checkbox"
                          aria-checked={ok}
                          aria-label={`${label}: ${ok ? 'met' : 'not met'}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: 14,
                            color: COLORS.text,
                            cursor: 'default',
                          }}
                        >
                          <Box
                            component="span"
                            aria-hidden="true"
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '3px',
                              flexShrink: 0,
                              border: `2px solid ${ok ? COLORS.success : '#9ca3af'}`,
                              backgroundColor: ok ? COLORS.success : '#fff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {ok && (
                              <svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">
                                <path
                                  d="M1 3.5L3.5 6L8 1"
                                  stroke="#fff"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </Box>
                          {label}
                        </Box>
                      ))}
                    </Box>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disableElevation
                      disabled={loading || !validateEmailSignUpFields()}
                      sx={{
                        mt: 2, mb: 1,
                        backgroundColor: validateEmailSignUpFields() ? COLORS.primary : COLORS.disabled,
                        borderRadius: '4px',
                        py: 1.35,
                        fontWeight: 700,
                        fontSize: 15,
                        textTransform: 'none',
                        '&:hover': { backgroundColor: validateEmailSignUpFields() ? COLORS.primaryHover : COLORS.disabled },
                        '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: '#fff' },
                      }}
                    >
                      {loading ? 'Signing up...' : 'Sign up'}
                    </Button>
                  </Box>

                  <Button
                    fullWidth
                    onClick={() => {
                      setWizardStep(0);
                    }}
                    sx={{ mt: 1, textTransform: 'none', color: COLORS.textMuted }}
                    aria-label="Go back to step 1"
                  >
                    Go Back
                  </Button>
                </Box>
              )}

              {signUpSuccess && (
                <Box
                  component="form"
                  onSubmit={handleVerificationCodeSubmit}
                  noValidate
                  sx={{ width: '100%' }}
                  aria-label="Email verification form"
                  aria-busy={loading}
                >
                  <Box aria-live="polite" aria-atomic="true">
                    <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>
                      Check your email for the verification code.
                    </Alert>
                  </Box>
                  <TextField
                    required
                    fullWidth
                    id="verification-code"
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    inputProps={{ 'aria-required': true }}
                    sx={{ ...fieldSx, mb: 2 }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                    disabled={loading}
                    sx={{ backgroundColor: COLORS.primary, borderRadius: '4px', py: 1.35, fontWeight: 700, fontSize: 15, textTransform: 'none', '&:hover': { backgroundColor: COLORS.primaryHover } }}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </Button>
                </Box>
              )}

              <Box
                component="footer"
                sx={{ mt: 4, textAlign: 'center', color: COLORS.textMuted, fontSize: 13, lineHeight: 1.6, fontFamily: FONT_FAMILY }}
              >
                By signing up, you accept our{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  target="talk-trees-privacy"
                  rel="noreferrer"
                  style={{ color: COLORS.primary, fontWeight: 600, textDecoration: 'underline', fontFamily: 'inherit' }}
                >
                  Privacy Policy
                </a> and how we keep your information safe.
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}